/**
 * Types for extension update management.
 */

export type ReleaseChannel = "dev" | "prod";

export type UpdateStatus = "none" | "optional" | "required";

/**
 * Response from backend /extension/release-info endpoint.
 */
export interface ReleaseInfo {
  latest_version: string;
  min_required_version: string;
  download_url: string;
  release_date: string;
  channel: string;
}

/**
 * Result of checking for extension updates.
 * currentVersion is null when extension is not installed (first-time install).
 */
export interface UpdateCheckResult {
  status: UpdateStatus;
  currentVersion: string | null;
  latestVersion: string;
  downloadUrl: string;
  channel: ReleaseChannel;
}

/**
 * Extension connection status.
 */
export type ExtensionConnectionStatus =
  | "unknown"
  | "connected"
  | "not_installed"
  | "connection_failed";

/**
 * Extension status info.
 */
export interface ExtensionStatus {
  connectionStatus: ExtensionConnectionStatus;
  version: string | null;
  channel: ReleaseChannel; // Defaults to 'prod' when not installed
  updateStatus: UpdateStatus;
  updateInfo: UpdateCheckResult | null;
  /** Re-check extension status. Returns true if connected. */
  recheckExtension: (useRetry?: boolean) => Promise<boolean>;
  /** Re-check with retry mechanism (better for after install/update). */
  recheckExtensionWithRetry: () => Promise<boolean>;
}

/**
 * Error types for extension download operations.
 */
export type DownloadErrorType =
  | "url_expired"
  | "network_error"
  | "access_denied"
  | "unknown";

/**
 * User-friendly error messages for download failures.
 */
export const DOWNLOAD_ERROR_MESSAGES: Record<DownloadErrorType, string> = {
  url_expired:
    "The download link has expired. Please refresh the page and try again.",
  network_error:
    "Network error while downloading. Please check your connection, refresh the page, and try again.",
  access_denied:
    "Access denied. Please refresh the page to get a new download link.",
  unknown: "Download failed. Please refresh the page and try again.",
};
